/**
 * PBP Bundle Builder
 * 3-part bottle builder: Hat Moodcap + Perfume + Base Moodcap
 * Hat and Base are fully independent — different shape & material each.
 * Infinite sliding carousel: items loop endlessly, 3 visible at a time.
 * Zone image sits centered on top of the selected carousel item.
 */
(function () {
  'use strict';

  const dataEl = document.getElementById('pbp-bundle-data');
  if (!dataEl) return;

  const DATA = JSON.parse(dataEl.textContent);

  // ── State — Hat and Base are independent ──
  const state = {
    activeZone: 'hat',
    hat: {
      shape: '',
      material: '',
      variantId: null,
      selectedIndex: 0,
      totalItems: 0,
      _needsReset: false,
    },
    base: {
      shape: '',
      material: '',
      variantId: null,
      selectedIndex: 0,
      totalItems: 0,
      _needsReset: false,
    },
  };

  // ── DOM refs ──
  const els = {
    hatImg: document.getElementById('pbp-hat-img'),
    baseImg: document.getElementById('pbp-base-img'),
    hatZone: document.getElementById('pbp-hat-zone'),
    baseZone: document.getElementById('pbp-base-zone'),
    hatCarousel: document.getElementById('pbp-hat-carousel'),
    baseCarousel: document.getElementById('pbp-base-carousel'),
    hatMaterialName: document.getElementById('pbp-hat-material-name'),
    baseMaterialName: document.getElementById('pbp-base-material-name'),
    hatMaterialBtns: document.getElementById('pbp-hat-material-btns'),
    baseMaterialBtns: document.getElementById('pbp-base-material-btns'),
    hatShapeStrip: document.getElementById('pbp-hat-shape-strip'),
    baseShapeStrip: document.getElementById('pbp-base-shape-strip'),
    hatShapeName: document.getElementById('pbp-hat-shape-name'),
    baseShapeName: document.getElementById('pbp-base-shape-name'),
    addToCartBtn: document.getElementById('pbp-add-to-cart'),
    totalPrice: document.getElementById('pbp-total-price'),
  };

  // ── Helpers ──
  function getUniqueShapes(variants) {
    const seen = new Set();
    return variants.filter((v) => {
      if (seen.has(v.shape)) return false;
      seen.add(v.shape);
      return true;
    });
  }

  function getUniqueMaterials(variants) {
    const seen = new Set();
    return variants
      .map((v) => v.material)
      .filter((m) => {
        if (!m || seen.has(m)) return false;
        seen.add(m);
        return true;
      });
  }

  function getMaterialsForShape(zone) {
    return DATA[zone].variants.filter((v) => v.shape === state[zone].shape);
  }

  function getShapesForMaterial(zone) {
    return DATA[zone].variants.filter((v) => v.material === state[zone].material);
  }

  function findVariant(variants, shape, material) {
    return (
      variants.find((v) => v.shape === shape && v.material === material && v.available) ||
      variants.find((v) => v.shape === shape && v.available)
    );
  }

  function formatMoney(cents) {
    const format = DATA.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toLocaleString('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return format
      .replace('{{amount_with_comma_separator}}', amount)
      .replace('{{amount_no_decimals_with_comma_separator}}', amount)
      .replace('{{amount_no_decimals}}', amount)
      .replace('{{amount}}', amount);
  }

  // ── Slide to a specific track index (position in the 3x item array) ──
  function slideToTrackIndex(zone, trackIdx, animate) {
    var carousel = els[zone + 'Carousel'];
    var zoneCenter = carousel ? carousel.parentElement : null;
    if (!carousel || !zoneCenter) return;

    var items = carousel.querySelectorAll('.pbp-builder__carousel-item');
    if (items.length === 0) return;

    var containerWidth = zoneCenter.offsetWidth;
    var itemWidth = items[0].offsetWidth;
    var computedGap = parseFloat(getComputedStyle(carousel).gap) || 8;
    var step = itemWidth + computedGap;

    // Center the target item in the container
    var targetCenter = trackIdx * step + itemWidth / 2;
    var offset = targetCenter - containerWidth / 2;

    if (animate === false) {
      carousel.style.transition = 'none';
    } else {
      carousel.style.transition = 'transform 0.35s ease';
    }

    carousel.style.transform = 'translateX(' + (-offset) + 'px) translateY(-50%)';

    if (animate === false) {
      carousel.offsetHeight; // force reflow
      carousel.style.transition = 'transform 0.35s ease';
    }
  }

  // ── Slide to the middle copy of the selected item (default position) ──
  function slideCarouselToMiddle(zone, animate) {
    var totalItems = state[zone].totalItems;
    var copies = state[zone].copies || 5;
    var middleCopy = Math.floor(copies / 2);
    var middleIdx = middleCopy * totalItems + state[zone].selectedIndex;
    slideToTrackIndex(zone, middleIdx, animate);
  }

  // ── After transition ends, reset to middle copy if on a clone ──
  function handleInfiniteReset(zone) {
    if (!state[zone]._needsReset) return;
    state[zone]._needsReset = false;
    slideCarouselToMiddle(zone, false);
  }

  // ── Highlight the selected item across all copies ──
  function highlightSelected(zone) {
    var carousel = els[zone + 'Carousel'];
    var items = carousel.querySelectorAll('.pbp-builder__carousel-item');
    var selectedIdx = state[zone].selectedIndex;

    items.forEach(function (item) {
      item.classList.toggle('is-selected', parseInt(item.dataset.realIndex) === selectedIdx);
    });
  }

  // ── Build Shape Strip — show ALL shapes, dim unavailable for current Material ──
  function buildShapeStrip(zone) {
    var variants = DATA[zone].variants;
    var allShapes = getUniqueShapes(variants);
    var shapesForMaterial = getShapesForMaterial(zone);
    var availableShapes = new Set(shapesForMaterial.map(function (v) { return v.shape; }));
    var currentShape = state[zone].shape;

    var strip = els[zone + 'ShapeStrip'];
    if (!strip) return;
    strip.innerHTML = '';

    allShapes.forEach(function (shapeVariant) {
      var v = findVariant(variants, shapeVariant.shape, state[zone].material) || shapeVariant;
      var isAvailable = availableShapes.has(v.shape);

      var thumb = document.createElement('div');
      thumb.className = 'pbp-builder__shape-thumb'
        + (v.shape === currentShape ? ' is-selected' : '')
        + (!isAvailable ? ' is-unavailable' : '');
      thumb.dataset.shape = v.shape;
      thumb.innerHTML = '<img src="' + (v.thumb || v.image) + '" alt="' + v.shape + '">';
      if (isAvailable) {
        thumb.addEventListener('click', function () { selectShapeForZone(zone, v.shape); });
      }
      strip.appendChild(thumb);
    });

    var nameEl = els[zone + 'ShapeName'];
    if (nameEl) {
      nameEl.textContent = currentShape ? '— ' + currentShape : '';
    }
  }

  // ── Build material → thumbnail map (first variant image per material) ──
  function getMaterialThumbMap(zone) {
    var map = {};
    DATA[zone].variants.forEach(function (v) {
      if (v.material && !map[v.material] && v.thumb) {
        map[v.material] = v.thumb;
      }
    });
    return map;
  }

  // ── Build Material Buttons — thumbnails, 4 per row, all always clickable (primary selector) ──
  function buildMaterialBtns(zone) {
    var container = els[zone + 'MaterialBtns'];
    if (!container) return;

    var allMaterials = getUniqueMaterials(DATA[zone].variants);
    var currentMaterial = state[zone].material;
    var thumbMap = getMaterialThumbMap(zone);

    container.innerHTML = '';

    allMaterials.forEach(function (mat) {
      var btn = document.createElement('button');
      btn.className = 'pbp-builder__material-btn'
        + (mat === currentMaterial ? ' is-selected' : '');
      btn.dataset.material = mat;

      var thumbSrc = thumbMap[mat];
      if (thumbSrc) {
        var img = document.createElement('img');
        img.src = thumbSrc;
        img.alt = mat;
        img.loading = 'lazy';
        btn.appendChild(img);
      }
      var label = document.createElement('span');
      label.className = 'pbp-builder__material-label';
      label.textContent = mat;
      btn.appendChild(label);

      btn.addEventListener('click', function () { selectMaterialForZone(zone, mat); });
      container.appendChild(btn);
    });
  }

  // ── Build Carousel — multiple copies for seamless infinite loop ──
  function buildCarousel(zone) {
    var carousel = els[zone + 'Carousel'];
    carousel.innerHTML = '';

    var materialsForShape = getMaterialsForShape(zone);
    if (materialsForShape.length === 0) return;

    var currentMaterial = state[zone].material;
    var selectedIdx = materialsForShape.findIndex(function (v) { return v.material === currentMaterial; });
    if (selectedIdx < 0) selectedIdx = 0;
    state[zone].selectedIndex = selectedIdx;
    state[zone].totalItems = materialsForShape.length;

    // Use enough copies so items always fill the visible area
    // For 2 items we need more copies; for many items 5 is plenty
    var copies = Math.max(5, Math.ceil(12 / materialsForShape.length) * 2 + 1);
    // Always make it odd so there's a clear middle
    if (copies % 2 === 0) copies++;
    state[zone].copies = copies;

    var middleCopy = Math.floor(copies / 2);

    for (var copy = 0; copy < copies; copy++) {
      materialsForShape.forEach(function (v, idx) {
        var isSelected = (parseInt(idx) === selectedIdx);

        var item = document.createElement('div');
        item.className = 'pbp-builder__carousel-item' + (isSelected ? ' is-selected' : '');
        item.dataset.material = v.material;
        item.dataset.variantId = v.id;
        item.dataset.realIndex = idx;
        item.innerHTML = '<img src="' + (v.image || v.thumb) + '" alt="' + v.title + '">';
        item.addEventListener('click', function () {
          selectMaterialForZone(zone, v.material);
        });
        carousel.appendChild(item);
      });
    }

    updateMaterialLabel(zone);

    // Position at middle copy immediately (no animation)
    requestAnimationFrame(function () {
      slideCarouselToMiddle(zone, false);
    });

    // Listen for transition end to handle infinite reset
    if (!carousel._transitionBound) {
      carousel._transitionBound = true;
      carousel.addEventListener('transitionend', function () {
        handleInfiniteReset(zone);
      });
    }
  }

  // ── Update material name in bottom picker ──
  function updateMaterialLabel(zone) {
    var nameEl = els[zone + 'MaterialName'];
    if (nameEl) {
      nameEl.textContent = state[zone].material ? '— ' + state[zone].material : '';
    }
  }

  // ── Navigate carousel by arrow direction (infinite loop) ──
  function navigateCarousel(zone, direction) {
    var materialsForShape = getMaterialsForShape(zone);
    var totalItems = materialsForShape.length;
    if (totalItems === 0) return;

    var copies = state[zone].copies || 5;
    var middleCopy = Math.floor(copies / 2);

    var oldIdx = state[zone].selectedIndex;
    var newIdx = oldIdx + direction;
    var displayIdx;

    if (newIdx >= totalItems) {
      // Wrapping forward: slide to first item in next copy
      newIdx = 0;
      displayIdx = (middleCopy + 1) * totalItems; // first item of copy after middle
      state[zone]._needsReset = true;
    } else if (newIdx < 0) {
      // Wrapping backward: slide to last item in previous copy
      newIdx = totalItems - 1;
      displayIdx = (middleCopy - 1) * totalItems + (totalItems - 1); // last item of copy before middle
      state[zone]._needsReset = true;
    } else {
      // Normal: slide to item in middle copy
      displayIdx = middleCopy * totalItems + newIdx;
      state[zone]._needsReset = false;
    }

    state[zone].selectedIndex = newIdx;
    state[zone].material = materialsForShape[newIdx].material;

    // Update all UI
    resolveVariant(zone);
    highlightSelected(zone);
    updateMaterialLabel(zone);
    buildMaterialBtns(zone);
    updatePreviewImage(zone);
    updateTotalPrice();
    validateStock();

    // Slide to display position (animated)
    slideToTrackIndex(zone, displayIdx, true);
  }

  // ── Swipe Support — bound to entire zone-row ──
  function setupSwipe(zone) {
    var carousel = els[zone + 'Carousel'];
    var zoneCenter = carousel ? carousel.parentElement : null;
    var zoneRow = zoneCenter ? zoneCenter.parentElement : null;
    if (!zoneRow) return;

    if (zoneRow._swipeBound) return;
    zoneRow._swipeBound = true;

    var startX = 0;
    var startY = 0;
    var isDragging = false;

    zoneRow.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    zoneRow.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      var dx = Math.abs(e.touches[0].clientX - startX);
      var dy = Math.abs(e.touches[0].clientY - startY);
      if (dx > dy && dx > 10) {
        e.preventDefault();
      }
    }, { passive: false });

    zoneRow.addEventListener('touchend', function (e) {
      if (!isDragging) return;
      isDragging = false;
      var endX = e.changedTouches[0].clientX;
      var diff = startX - endX;
      var threshold = 40;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          navigateCarousel(zone, 1);
        } else {
          navigateCarousel(zone, -1);
        }
      }
    }, { passive: true });
  }

  // ── Selection Logic ──
  function setActiveZone(zone) {
    state.activeZone = zone;
  }

  function selectShapeForZone(zone, shape) {
    state[zone].shape = shape;

    // Material stays the same — just find the variant for this shape + current material
    var materialsForShape = getMaterialsForShape(zone);
    var idx = materialsForShape.findIndex(function (v) { return v.material === state[zone].material; });
    state[zone].selectedIndex = idx >= 0 ? idx : 0;

    resolveVariant(zone);
    buildShapeStrip(zone);
    buildCarousel(zone);
    updateMaterialLabel(zone);
    updatePreviewImage(zone);
    updateTotalPrice();
    validateStock();
  }

  function selectMaterialForZone(zone, material) {
    state[zone].material = material;

    // Material is primary — check if current shape is still available for this material
    var shapesForMaterial = getShapesForMaterial(zone);
    var shapeNames = shapesForMaterial.map(function (v) { return v.shape; });
    if (shapeNames.indexOf(state[zone].shape) < 0 && shapesForMaterial.length > 0) {
      // Current shape not available — auto-select first available shape
      state[zone].shape = shapesForMaterial[0].shape;
    }

    var materialsForShape = getMaterialsForShape(zone);
    var idx = materialsForShape.findIndex(function (v) { return v.material === material; });
    state[zone].selectedIndex = idx >= 0 ? idx : 0;
    state[zone]._needsReset = false;

    resolveVariant(zone);
    buildShapeStrip(zone);
    buildCarousel(zone);
    highlightSelected(zone);
    slideCarouselToMiddle(zone, true);
    updateMaterialLabel(zone);
    buildMaterialBtns(zone);
    updatePreviewImage(zone);
    updateTotalPrice();
    validateStock();
  }

  function resolveVariant(zone) {
    var v = findVariant(DATA[zone].variants, state[zone].shape, state[zone].material);
    state[zone].variantId = v ? v.id : null;
  }

  function updatePreviewImage(zone) {
    var v = findVariant(DATA[zone].variants, state[zone].shape, state[zone].material);
    var img = zone === 'hat' ? els.hatImg : els.baseImg;
    if (v && v.image && img) {
      img.src = v.image;
    }
  }

  function updateTotalPrice() {
    var perfumePrice = DATA.perfume.price || 0;
    var hatV = findVariant(DATA.hat.variants, state.hat.shape, state.hat.material);
    var baseV = findVariant(DATA.base.variants, state.base.shape, state.base.material);
    var hatPrice = hatV ? hatV.price : 0;
    var basePrice = baseV ? baseV.price : 0;
    var total = perfumePrice + hatPrice + basePrice;
    if (els.totalPrice) {
      els.totalPrice.textContent = 'Total: ' + formatMoney(total);
    }
  }

  // ── Stock Validation ──
  function validateStock() {
    var warningEl = document.getElementById('pbp-stock-warning');
    var cartBtn = els.addToCartBtn;
    if (!warningEl || !cartBtn) return;

    var qtyEl = document.getElementById('pbp-qty-input');
    var qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
    var issues = [];

    // Check perfume stock
    var perfume = DATA.perfume;
    if (perfume.inventory_policy !== 'continue') {
      if (!perfume.available) {
        issues.push('Perfume is out of stock.');
      } else if (perfume.inventory_quantity < qty) {
        issues.push('Perfume only has ' + perfume.inventory_quantity + ' in stock.');
      }
    }

    // Check hat variant stock
    var hatV = findVariant(DATA.hat.variants, state.hat.shape, state.hat.material);
    if (hatV && hatV.inventory_policy !== 'continue') {
      if (!hatV.available) {
        issues.push('Hat Cap (' + state.hat.shape + ' / ' + state.hat.material + ') is out of stock. Please pick a different Hat Cap.');
      } else if (hatV.inventory_quantity < qty) {
        issues.push('Hat Cap (' + state.hat.shape + ' / ' + state.hat.material + ') only has ' + hatV.inventory_quantity + ' in stock.');
      }
    }

    // Check base variant stock
    var baseV = findVariant(DATA.base.variants, state.base.shape, state.base.material);
    if (baseV && baseV.inventory_policy !== 'continue') {
      if (!baseV.available) {
        issues.push('Base Cap (' + state.base.shape + ' / ' + state.base.material + ') is out of stock. Please pick a different Base Cap.');
      } else if (baseV.inventory_quantity < qty) {
        issues.push('Base Cap (' + state.base.shape + ' / ' + state.base.material + ') only has ' + baseV.inventory_quantity + ' in stock.');
      }
    }

    if (issues.length > 0) {
      warningEl.innerHTML = '<strong>Stock issue:</strong> ' + issues.join('<br>') + '<br>Please adjust your selection or reduce the quantity.';
      warningEl.style.display = 'block';
      cartBtn.disabled = true;
    } else {
      warningEl.style.display = 'none';
      warningEl.innerHTML = '';
      cartBtn.disabled = false;
    }
  }

  // ── Zone Click — set active zone ──
  function handleZoneClick(zone) {
    setActiveZone(zone);
  }

  // ── Carousel Nav Arrows ──
  document.querySelectorAll('.pbp-builder__carousel-nav').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var zone = btn.dataset.carousel;
      var dir = btn.dataset.dir === 'prev' ? -1 : 1;
      navigateCarousel(zone, dir);
    });
  });

  // ── Zone Click Handlers ──
  if (els.hatZone) {
    els.hatZone.addEventListener('click', function () { handleZoneClick('hat'); });
  }
  if (els.baseZone) {
    els.baseZone.addEventListener('click', function () { handleZoneClick('base'); });
  }

  // ── Download Composite Image ──
  var downloadBtn = document.getElementById('pbp-download-btn');
  var compositeCanvas = document.getElementById('pbp-composite-canvas');

  if (downloadBtn && compositeCanvas) {
    downloadBtn.addEventListener('click', async function () {
      downloadBtn.textContent = 'Generating...';
      downloadBtn.disabled = true;

      try {
        var loadImage = function (src) {
          return new Promise(function (resolve, reject) {
            var img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () { resolve(img); };
            img.onerror = function () { reject(new Error('Failed to load image')); };
            img.src = src;
          });
        };

        var hatSrc = els.hatImg ? els.hatImg.src : '';
        var perfumeEl = document.getElementById('pbp-perfume-img');
        var perfumeSrc = perfumeEl ? perfumeEl.src : '';
        var baseSrc = els.baseImg ? els.baseImg.src : '';

        var images = await Promise.all([
          hatSrc ? loadImage(hatSrc) : null,
          perfumeSrc ? loadImage(perfumeSrc) : null,
          baseSrc ? loadImage(baseSrc) : null,
        ]);

        var hatImage = images[0];
        var perfumeImage = images[1];
        var baseImage = images[2];

        // Use actual screen positions via getBoundingClientRect
        // This perfectly replicates the on-screen layout including overlaps
        var perfumeZone = document.querySelector('.pbp-builder__zone--perfume');
        var hatZoneRect = els.hatZone ? els.hatZone.getBoundingClientRect() : null;
        var perfZoneRect = perfumeZone ? perfumeZone.getBoundingClientRect() : null;
        var baseZoneRect = els.baseZone ? els.baseZone.getBoundingClientRect() : null;

        // Scene bounding box from actual screen positions
        var rects = [hatZoneRect, perfZoneRect, baseZoneRect].filter(Boolean);
        var sceneTop = Math.min.apply(null, rects.map(function(r) { return r.top; }));
        var sceneBottom = Math.max.apply(null, rects.map(function(r) { return r.bottom; }));
        var sceneLeft = Math.min.apply(null, rects.map(function(r) { return r.left; }));
        var sceneRight = Math.max.apply(null, rects.map(function(r) { return r.right; }));
        var sceneW = sceneRight - sceneLeft;
        var sceneH = sceneBottom - sceneTop;

        // Scale up for high-res output (2x)
        var scale = 2;
        var padding = 20 * scale;
        var canvasW = sceneW * scale + padding * 2;
        var canvasH = sceneH * scale + padding * 2;

        var ctx = compositeCanvas.getContext('2d');
        compositeCanvas.width = canvasW;
        compositeCanvas.height = canvasH;

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasW, canvasH);

        // Helper: compute where image actually renders inside its zone (object-fit: contain)
        function getContainedRect(loadedImg, zoneRect) {
          var zw = zoneRect.width;
          var zh = zoneRect.height;
          var natW = loadedImg.naturalWidth;
          var natH = loadedImg.naturalHeight;
          var zoneRatio = zw / zh;
          var imgRatio = natW / natH;
          var drawW, drawH, drawX, drawY;
          if (imgRatio > zoneRatio) {
            drawW = zw;
            drawH = zw / imgRatio;
            drawX = zoneRect.left;
            drawY = zoneRect.top + (zh - drawH) / 2;
          } else {
            drawH = zh;
            drawW = zh * imgRatio;
            drawX = zoneRect.left + (zw - drawW) / 2;
            drawY = zoneRect.top;
          }
          return { x: drawX, y: drawY, w: drawW, h: drawH };
        }

        // Draw PERFUME FIRST (behind the caps)
        if (perfumeImage && perfZoneRect) {
          var pr = getContainedRect(perfumeImage, perfZoneRect);
          ctx.drawImage(perfumeImage,
            (pr.x - sceneLeft) * scale + padding,
            (pr.y - sceneTop) * scale + padding,
            pr.w * scale, pr.h * scale);
        }

        // Draw HAT CAP on top (in front of bottle)
        if (hatImage && hatZoneRect) {
          var hr = getContainedRect(hatImage, hatZoneRect);
          ctx.drawImage(hatImage,
            (hr.x - sceneLeft) * scale + padding,
            (hr.y - sceneTop) * scale + padding,
            hr.w * scale, hr.h * scale);
        }

        // Draw BASE CAP on top (in front of bottle)
        if (baseImage && baseZoneRect) {
          var br = getContainedRect(baseImage, baseZoneRect);
          ctx.drawImage(baseImage,
            (br.x - sceneLeft) * scale + padding,
            (br.y - sceneTop) * scale + padding,
            br.w * scale, br.h * scale);
        }

        var dataURL = compositeCanvas.toDataURL('image/png');
        var link = document.createElement('a');
        link.download = 'my-pagebypage-build.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        downloadBtn.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Saved!';
        setTimeout(function () {
          downloadBtn.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Save My Build';
          downloadBtn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('Download composite error:', error);
        downloadBtn.textContent = 'Error — try again';
        setTimeout(function () {
          downloadBtn.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Save My Build';
          downloadBtn.disabled = false;
        }, 2000);
      }
    });
  }

  // ── Quantity Selector ──
  var qtyInput = document.getElementById('pbp-qty-input');
  var qtyMinus = document.getElementById('pbp-qty-minus');
  var qtyPlus = document.getElementById('pbp-qty-plus');

  function getQuantity() {
    var val = parseInt(qtyInput ? qtyInput.value : 1, 10);
    return isNaN(val) || val < 1 ? 1 : val;
  }

  if (qtyMinus) {
    qtyMinus.addEventListener('click', function () {
      var val = getQuantity();
      if (val > 1) qtyInput.value = val - 1;
      validateStock();
    });
  }

  if (qtyPlus) {
    qtyPlus.addEventListener('click', function () {
      var val = getQuantity();
      if (val < 99) qtyInput.value = val + 1;
      validateStock();
    });
  }

  if (qtyInput) {
    qtyInput.addEventListener('change', function () {
      var val = parseInt(qtyInput.value, 10);
      if (isNaN(val) || val < 1) qtyInput.value = 1;
      if (val > 99) qtyInput.value = 99;
      validateStock();
    });
  }

  // ── Build Config Text (Option C) ──
  function getBuildConfigText() {
    var parts = [];
    parts.push('Hat Cap: ' + (state.hat.shape || '?') + ' / ' + (state.hat.material || '?'));
    parts.push('Base Cap: ' + (state.base.shape || '?') + ' / ' + (state.base.material || '?'));
    parts.push('Perfume: ' + (DATA.perfume.title || 'Bundle Perfume'));
    return parts.join(' | ');
  }

  // ── Add to Cart ──
  if (els.addToCartBtn) {
    els.addToCartBtn.addEventListener('click', async function () {
      var btn = els.addToCartBtn;
      if (btn.disabled) return;

      var qty = getQuantity();
      var buildConfig = getBuildConfigText();

      // Build items with line item properties (Option C)
      var items = [];

      if (DATA.perfume.variantId) {
        items.push({
          id: DATA.perfume.variantId,
          quantity: qty,
          properties: { '_Bundle Build': buildConfig, '_Bundle Role': 'Perfume' }
        });
      }

      if (state.hat.variantId) {
        items.push({
          id: state.hat.variantId,
          quantity: qty,
          properties: { '_Bundle Build': buildConfig, '_Bundle Role': 'Hat Cap' }
        });
      }

      if (state.base.variantId) {
        items.push({
          id: state.base.variantId,
          quantity: qty,
          properties: { '_Bundle Build': buildConfig, '_Bundle Role': 'Base Cap' }
        });
      }

      if (items.length === 0) {
        alert('Please select your options first.');
        return;
      }

      btn.disabled = true;
      btn.textContent = 'ADDING...';

      console.log('PBP Bundle: Adding to cart', JSON.stringify(items));

      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items }),
        });

        var result = await response.json();
        console.log('PBP Bundle: Cart response', response.status, JSON.stringify(result));

        if (response.ok) {
          btn.textContent = 'ADDED TO CART ✓';

          document.dispatchEvent(new CustomEvent('cart:refresh'));

          try {
            var cartResponse = await fetch('/cart.js');
            var cartData = await cartResponse.json();
            var countEls = document.querySelectorAll(
              '.cart-count, [data-cart-count], .header-actions__cart-count'
            );
            countEls.forEach(function (el) {
              el.textContent = cartData.item_count;
            });
          } catch (e) {
            // Silently fail
          }

          setTimeout(function () {
            var cartDrawer = document.querySelector('cart-drawer, [data-cart-drawer]');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              window.location.href = '/cart';
            }
          }, 500);

          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = 'ADD TO CART';
          }, 2000);
        } else {
          throw new Error(result.message || result.description || 'Failed to add to cart');
        }
      } catch (error) {
        btn.textContent = 'ERROR - TRY AGAIN';
        console.error('PBP Bundle: Add to cart error:', error.message || error);
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = 'ADD TO CART';
        }, 2000);
      }
    });
  }

  // ── Reposition carousels on window resize ──
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      slideCarouselToMiddle('hat', false);
      slideCarouselToMiddle('base', false);
    }, 100);
  });

  // ── Init ──
  function init() {
    if (DATA.hat.variants.length === 0 && DATA.base.variants.length === 0) {
      console.warn('PBP Bundle Builder: No hat or base variants found.');
      return;
    }

    // Set initial selections for Hat
    if (DATA.hat.variants.length > 0) {
      var hatShapes = getUniqueShapes(DATA.hat.variants);
      var hatMaterials = getUniqueMaterials(DATA.hat.variants);
      state.hat.shape = hatShapes[0].shape;
      state.hat.material = hatMaterials[0] || '';
      state.hat.selectedIndex = 0;
      resolveVariant('hat');
      updatePreviewImage('hat');
    }

    // Set initial selections for Base (independently)
    if (DATA.base.variants.length > 0) {
      var baseShapes = getUniqueShapes(DATA.base.variants);
      var baseMaterials = getUniqueMaterials(DATA.base.variants);
      state.base.shape = baseShapes[0].shape;
      state.base.material = baseMaterials[0] || '';
      state.base.selectedIndex = 0;
      resolveVariant('base');
      updatePreviewImage('base');
    }

    // Build shape strips for both zones
    buildShapeStrip('hat');
    buildShapeStrip('base');

    // Build both carousels (3x copies for infinite loop)
    buildCarousel('hat');
    buildCarousel('base');

    // Setup swipe on zone rows
    setupSwipe('hat');
    setupSwipe('base');

    // Build material buttons and labels for both zones
    buildMaterialBtns('hat');
    buildMaterialBtns('base');
    updateMaterialLabel('hat');
    updateMaterialLabel('base');

    updateTotalPrice();
    validateStock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();